import type {
  ITicketConfigTour,
  TicketConfigTourPayload,
} from '@/shared/api/types/ticket-config-types'

/** Selected feature ids per tour id, as currently shown in the card. */
export type TourSelections = Record<string, string[]>

export function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

export function buildSelections(tours: ITicketConfigTour[]): TourSelections {
  return Object.fromEntries(tours.map((tour) => [tour.tourId, tour.featureIds]))
}

export function toggleFeature(current: string[], featureId: string, checked: boolean): string[] {
  return checked
    ? Array.from(new Set([...current, featureId]))
    : current.filter((id) => id !== featureId)
}

// * VI: Tour chỉ lưu override khi tập tính năng khác cấu hình chung; trùng thì bỏ override để tour tiếp tục theo cấu hình chung khi cấu hình đó đổi | EN: A tour only keeps an override when its features differ from the config-level ones; an identical set drops the override so the tour keeps following the config if it changes later.
export function buildOverridePayload(
  tours: ITicketConfigTour[],
  selections: TourSelections,
  defaultFeatureIds: string[],
): TicketConfigTourPayload[] {
  return tours.flatMap((tour) => {
    const featureIds = selections[tour.tourId] ?? tour.featureIds
    return sameIds(featureIds, defaultFeatureIds) ? [] : [{ tourId: tour.tourId, featureIds }]
  })
}

export function hasUnsavedChanges(
  tours: ITicketConfigTour[],
  selections: TourSelections,
  defaultFeatureIds: string[],
): boolean {
  const next = buildOverridePayload(tours, selections, defaultFeatureIds)
  const saved = tours.filter((tour) => tour.isCustomized)
  return (
    next.length !== saved.length ||
    next.some((item) => {
      const current = saved.find((tour) => tour.tourId === item.tourId)
      return !current || !sameIds(current.featureIds, item.featureIds)
    })
  )
}
